/**
 * Team Comparison Panel
 * Side-by-side stats and charts for comparing teams
 */

import * as d3 from 'd3';

export class TeamPanelController {
  constructor() {
    this.panel = document.getElementById('comparison-panel');
    this.grid = document.getElementById('comparison-grid');
    this.chart = document.getElementById('comparison-chart');
    this.content = document.getElementById('comparison-content');
    this.selectMessage = this.panel.querySelector('.comparison-select');
    this.toggleBtn = document.getElementById('toggle-comparison');
    this.closeBtn = document.getElementById('close-comparison');

    this.teams = [];

    this.init();
  }

  init() {
    // Toggle panel
    this.toggleBtn.addEventListener('click', () => this.toggle());

    // Close button
    this.closeBtn.addEventListener('click', () => this.hide());

    // Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.panel.classList.contains('hidden')) {
        this.hide();
      }
    });
  }

  /**
   * Toggle panel visibility
   */
  toggle() {
    this.panel.classList.toggle('hidden');
    this.toggleBtn.classList.toggle('active');
  }

  /**
   * Show panel
   */
  show() {
    this.panel.classList.remove('hidden');
    this.toggleBtn.classList.add('active');
  }

  /**
   * Hide panel
   */
  hide() {
    this.panel.classList.add('hidden');
    this.toggleBtn.classList.remove('active');
  }

  /**
   * Update comparison with selected teams
   */
  update(teams) {
    this.teams = teams;

    if (teams.length === 0) {
      this.content.classList.add('hidden');
      this.selectMessage.style.display = 'block';
      return;
    }

    this.selectMessage.style.display = 'none';
    this.content.classList.remove('hidden');

    this.renderGrid();
    this.renderChart();
  }

  /**
   * Render comparison grid
   */
  renderGrid() {
    this.grid.innerHTML = '';

    this.teams.forEach(team => {
      const pos = team.interpolated || team.latest;
      const stats = team.stats || {};

      const card = document.createElement('div');
      card.className = 'comparison-team';
      card.style.setProperty('--team-color', team.color);

      card.innerHTML = `
        <div class="comparison-team-name">${this.escapeHtml(team.name)}</div>
        <div class="comparison-stats">
          <div class="comparison-stat">
            <span class="comparison-stat-label">Distance to Finish</span>
            <span class="comparison-stat-value">${(pos?.dtf || 0).toFixed(0)} nm</span>
          </div>
          <div class="comparison-stat">
            <span class="comparison-stat-label">Current Speed</span>
            <span class="comparison-stat-value">${(pos?.speed || 0).toFixed(1)} kn</span>
          </div>
          <div class="comparison-stat">
            <span class="comparison-stat-label">Avg Speed</span>
            <span class="comparison-stat-value">${stats.avgSpeed || '--'} kn</span>
          </div>
          <div class="comparison-stat">
            <span class="comparison-stat-label">Max Speed</span>
            <span class="comparison-stat-value">${stats.maxSpeed || '--'} kn</span>
          </div>
          <div class="comparison-stat">
            <span class="comparison-stat-label">Course</span>
            <span class="comparison-stat-value">${Math.round(pos?.cog || 0)}°</span>
          </div>
          <div class="comparison-stat">
            <span class="comparison-stat-label">Battery</span>
            <span class="comparison-stat-value">${pos?.battery !== undefined ? pos.battery + '%' : '--'}</span>
          </div>
        </div>
      `;

      this.grid.appendChild(card);
    });
  }

  /**
   * Render speed comparison chart
   */
  renderChart() {
    // Clear previous chart
    this.chart.innerHTML = '';

    if (this.teams.length === 0) return;

    const margin = { top: 20, right: 20, bottom: 30, left: 40 };
    const width = this.chart.clientWidth - margin.left - margin.right;
    const height = 120 - margin.top - margin.bottom;

    if (width <= 0 || height <= 0) return;

    const svg = d3.select(this.chart)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Prepare data - last 24 hours of speed data
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const allData = [];
    this.teams.forEach(team => {
      const recentPositions = team.positions.filter(p => p.timestamp >= oneDayAgo);
      recentPositions.forEach(pos => {
        allData.push({
          team: team.name,
          color: team.color,
          time: pos.timestamp,
          speed: pos.speed,
        });
      });
    });

    if (allData.length === 0) {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', 'rgba(255,255,255,0.4)')
        .attr('font-size', '12px')
        .text('No recent data');
      return;
    }

    // Scales
    const xScale = d3.scaleTime()
      .domain([oneDayAgo, now])
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(allData, d => d.speed) * 1.1])
      .range([height, 0]);

    // Axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(4).tickFormat(d3.timeFormat('%H:%M')))
      .attr('color', 'rgba(255,255,255,0.3)')
      .attr('font-size', '9px');

    svg.append('g')
      .call(d3.axisLeft(yScale).ticks(3))
      .attr('color', 'rgba(255,255,255,0.3)')
      .attr('font-size', '9px');

    // Lines for each team
    const line = d3.line()
      .x(d => xScale(d.time))
      .y(d => yScale(d.speed))
      .curve(d3.curveMonotoneX);

    this.teams.forEach(team => {
      const teamData = allData
        .filter(d => d.team === team.name)
        .sort((a, b) => a.time - b.time);

      if (teamData.length > 1) {
        svg.append('path')
          .datum(teamData)
          .attr('fill', 'none')
          .attr('stroke', team.color)
          .attr('stroke-width', 2)
          .attr('stroke-opacity', 0.8)
          .attr('d', line);
      }
    });
  }

  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
